import { AxiosRequestConfig } from 'axios';
import firebase from 'firebase/compat/app';
import _, {
    groupBy,
    pick,
} from 'lodash';

import {
    AltinnAttachementsData,
    ClientError,
} from '../';
import { Settings } from '../../providers';
import { Client } from '../Client';

export type RequestID = string;

interface Source {
    descr: string;
    idSource: number;
    logo: string;
    source: 'altinn' | string;
    type: 'tax' | string;
}

export interface User {
    IP: string;
    activated: boolean;
    address: string;
    created: string;
    email: string;
    emailVerified: number;
    id: string;
    lang: string;
    marketingConsent: boolean;
    name: string;
    paymentRemark: number;
    phoneNumber: string;
    photoUrl?: string;
    sources: Source[];
    useragent: string;
    zipCode: string;
    role: string;
    login: string;
    updated: string;
    ssn: string;
    idUser: string;
}

export class UpscoreClient extends Client {
    protected firebaseUserIdToken?: string;
    protected theiaAuthToken?: string;
    // protected bankIDAccessToken?: string;

    constructor(
        requestConfig: AxiosRequestConfig,
        project: Settings['languageVersion'],
    ) {
        super(requestConfig);

        /**
         * These configs will be passed to EVERY request that goes through
         * Upscore API Client.
         */
        this.client.interceptors.request.use(
            async (config) => {
                const { params, method, data, headers } = config;
                /**
                 * Data needs to be parsed into `FormData` format before it is
                 * sent to backend.
                 *
                 * We also want include requestID (as additinal data parameter)
                 * to every backend request
                 */
                const requestID = config.url?.includes(`/user/requestID`)
                    ? null
                    : await this.getUserRequestID();

                if (
                    method === 'get' &&
                    !config.url?.includes(`/user/requestID`)
                ) {
                    config.params = { ...params, project, requestID };
                }

                /**
                 * TODO: Remove `skipFormDataParse` header when we split the project. We should not parseFormData every POST/PUT request that does have `data`
                 */
                if (headers && !headers['x-skip-formparse']) {
                    config.data = this.parseToFormData({
                        ...data,
                        project,
                        requestID,
                    });
                }

                if (headers && headers['x-skip-formparse']) {
                    delete headers['x-skip-formparse'];
                }

                /**
                 * Send an additional header `Authorization` when the user is
                 * logged in to our auth service (Firebase).
                 */
                this.firebaseUserIdToken = await firebase
                    .app()
                    .auth()
                    .currentUser?.getIdToken();

                if (this.firebaseUserIdToken) {
                    config.headers = {
                        ...config.headers,
                        Authorization: this.firebaseUserIdToken,
                    };
                }

                this.theiaAuthToken =
                    sessionStorage.getItem('theia-token') || undefined;

                if (this.theiaAuthToken) {
                    config.headers = {
                        ...config.headers,
                        'Theia-Authorization': `Bearer ${this.theiaAuthToken}`,
                    };
                }

                // this.bankIDAccessToken =
                //     sessionStorage.getItem('accessToken') || undefined;

                // if (this.bankIDAccessToken) {
                //     config.headers = {
                //         ...headers,
                //         'x-auth-method': 'oauth2',
                //         Authorization: `Bearer ${this.bankIDAccessToken}`,
                //     };
                // }

                return config;
            },
            (error) => Promise.reject(error),
        );
    }

    /**
     * Once we retrieve an authorization token from UpscoreAPI
     * postAuthIdportenVerify we need to make sure that every call to AltinnAPI
     * will contain this token in `Authorization': 'Bearer ${access_token}`
     * format.
     */
    public setAuthorizationAccessToken = (token: string) =>
        this.client.interceptors.request.use((config) => {
            if (!config || !config.headers) {
                return;
            }

            config.headers['Theia-Authorization'] = `Bearer ${token}`;

            return config;
        });

    public credentialsAvailable = async () => {
        const token = this.firebaseUserIdToken;
        const identity = await this.getUserRequestID();

        if (!token || !identity) {
            return false;
        }

        return true;
    };

    /**
     * IDPorten authentication url which is generated dynamically in backend.
     *
     * For `redirectUrl`we are using `window.location.origin + 'altinn-process'`
     * because this param needs to be whitelisted/predefined.
     *
     * List of currently whitelisted urls:
     * https://beta.upscore.no/altinn-process
     * https://qa.knowledgebase.no/altinn-process
     * https://formue-qa.knowledgebase.no/altinn-process
     * https://penge-qa.knowledgebase.no/altinn-process
     * https://inkasso-qa.knowledgebase.no/altinn-process
     * https://qa.inkassoregisteret.com/altinn-process
     * http://localhost:3000/altinn-process
     * http://localhost:3001/altinn-process
     * http://localhost:3002/altinn-process
     * http://localhost:3003/altinn-process
     * https://upscore.netlify.app/altinn-process
     * https://upscorestaging.netlify.app/altinn-process
     */
    public getAuthIdportenAuthurl = async (config: AxiosRequestConfig = {}) =>
        this.client
            .get<{ authUrl: string; requestID: string }>(
                `/auth/idporten/authurl`,
                config,
            )
            .then((response) => response.data);

    public postAuthIdportenVerify = async (params: {
        code: string;
        state: string;
    }) =>
        this.client
            .get<{ accessToken: string; requestID: string }>(
                `/auth/idporten/verify`,
                {
                    params,
                },
            )
            .then((response) => response.data);
    /**
     * Token-like string that is supposed to be generated per user session.
     * Some of the API calls might contain `sensitive` data and usually we need
     * to include `requestID` in the request body.
     *
     * We also store the value in browser sessionStorage as `upscore-request-id`
     * to prevent unecessary API calls.
     */
    public getUserRequestID = async () => {
        const storageRequestID = sessionStorage.getItem(`upscore-request-id`);

        if (storageRequestID) return storageRequestID;

        return await this.client
            .get<{
                requestID: RequestID;
            }>(`/user/requestID`, {
                withCredentials: true,
                headers: {
                    'Access-Control-Allow-Credentials': 'true',
                    'Content-Type': 'application/json',
                },
            })
            .then((response) => {
                sessionStorage.setItem(
                    `upscore-request-id`,
                    response.data.requestID,
                );
                return response.data.requestID;
            });
    };

    /**
     * TODO: Add type to scoreData
     */
    public getUserScoredata = () =>
        // axios
        //     .get('https://run.mocky.io/v3/446dfb6b-7ec2-476c-b3fe-c96e49f25ad2')
        // .then((response) => response.data);
        this.client
            .get<{ requestID: string; scoreData: any }>(`/user/scoredata`)
            .then((response) => response.data);

    public getFunfacts = async () =>
        this.client
            .get<
                Array<{
                    descr: string;
                    type: string;
                    var?: {
                        avgScore?: number;
                        topScore?: number;
                        country?: string;
                    };
                }>
            >(`/funfacts`)
            .then((response) => response.data);

    public getSourceTaxdataProgress = async () =>
        this.client
            .get<{
                refcode: string;
                requestID: RequestID;
                status: string;
                step: number;
            }>(`/source/taxdata/progress`)
            .then((response) => response.data);

    /**
     * Returns user profile data
     */
    public getUserProfile = () =>
        this.client
            .get<{
                requestID: RequestID;
                profile: {
                    address: string;
                    email: string;
                    lang: string;
                    marketingConsent: 0 | 1;
                    name: string;
                    // TODO: ADD TYPE
                    phoneNumber: any;
                    sources: Source[];
                };
            }>(`/user/profile`)
            .then((response) => {
                return response.data.profile;
            });

    /**
     *
     * TODO: Add types
     */
    public getSourceDebtregistryIframeurl = (data: {
        code: string;
        state: string;
    }) =>
        this.client
            .get(`/source/debtregistry/iframeurl`, { params: data })
            .then((response) => response.data);

    /**
     * Get Signicat sign document url
     */
    public getSignSignurl = (params: { redirectUrl: string }) =>
        this.client
            .get<{ signUrl: string }>(`/sign/signurl`, { params })
            .then((response) => response.data);

    /**
     * TODO: Add types
     */
    public getSourceDebtregistryAuthurl = (data: {
        authMethod: string;
        redirectUrl: string;
    }) =>
        this.client
            .get<{ authUrl: string }>(`/auth/bankid/authurl`, { params: data })
            .then((response) => response.data);

    /**
     * Temporary endpoint just for MVP of debtregistry
     * */
    public getSourceDebtregistryExtract = () =>
        this.client
            .get(`/source/debtregistry/extract`)
            .then((response) => response.data);

    public getProgressUpdate = async (): Promise<boolean> => {
        const status = {
            done: 'DONE',
            failed: 'FAILED',
            failedBankIDMobile: 'FAILED_BANKID_MOBILE',
        };
        const throwProgressError = (status = '', extras = {}) => {
            throw new ClientError(
                `Upscore API progress failed.${status && ` Status: ${status}`}`,
                {
                    category: 'Upscore API',
                    type: 'general',
                    extras,
                },
            );
        };

        return await this.getSourceTaxdataProgress()
            .then((response) => {
                if (response.status === status.failed) {
                    throwProgressError();
                }

                if (response.status === status.done) {
                    return true;
                }

                return false;
            })
            .catch((error) => {
                throwProgressError();
                return false;
            });
    };

    /**
     * Temporary endpoint just for MVP of debtregistry
     */
    public getSourceDebtregistryProgress = () =>
        this.client
            .get(`source/debtregistry/progress`)
            .then((response) => response.data);

    public postSourceTaxdataExtract = (data: {
        /** Stringified JSON credentials */
        credentials: string;
    }) =>
        this.client
            .post<{ message: string; requestID: RequestID }>(
                `/source/taxdata/extract`,
                data,
            )
            .then((response) => response.data)
            .catch((error) => {
                throw new ClientError('Upscore API extract failed', {
                    category: 'Upscore API',
                    type: 'general',
                    extras: {
                        error: JSON.stringify(error, null, 2),
                    },
                });
            });

    /**
     * Uploads a file as pdf to UpscoreAPI.
     */
    public postSourceTaxdataUpload = (
        data: Pick<
            AltinnAttachementsData,
            'file' | 'year' | 'target' | 'dataType' | 'fileName' | 'fileType'
        >,
    ) =>
        this.client.post(`/source/taxdata/upload`, data, {
            headers: {
                'Content-Type': 'application/pdf',
            },
            timeout: 30000,
        });

    /**
     * Before attachements/documents/files will be uploaded to Upscore API we
     * need to filter the data to make sure that we're not trying to upload a
     * document that we could not aquire from Altinn and we only want to pass
     * certain fields like: `file, year, target, type, fileName`
     */
    public uploadAltinnAttachements = async (
        attachementsData: AltinnAttachementsData[],
    ) => {
        return await Promise.all(
            attachementsData.map(async (attachement) => {
                if (!attachement || attachement.file === null) return;

                const dataToBeUploaded = pick(attachement, [
                    'file',
                    'year',
                    'target',
                    'dataType',
                    'fileName',
                    'fileType',
                ]);

                return await this.postSourceTaxdataUpload(
                    dataToBeUploaded,
                ).catch((error) => console.error(error));
            }),
        );
    };

    public postSourceTaxdataSummary = async (
        data: AltinnAttachementsData[],
    ) => {
        const groups = groupBy(data, 'target');
        const summaryData = [];

        for (const group in groups) {
            const documents = groups[group].map(
                ({ fileName, status, documentName }) => {
                    return { document: documentName, fileName, status };
                },
            );

            summaryData.push({
                entityId: group,
                entityType: groups[group][0].entityType,
                documents,
            });
        }

        return this.client.post(`/source/taxdata/summary`, {
            meta: JSON.stringify(summaryData),
            ...(sessionStorage.getItem('externalId') && {
                externalId: sessionStorage.getItem('externalId'),
            }),
            ...(sessionStorage.getItem('callbackUrl') && {
                callbackUrl: sessionStorage.getItem('callbackUrl'),
            }),
        });
    };

    /**
     * Returns a result with `boolean` value
     */
    public postContact = (data: {
        name: string;
        email: string;
        phoneNumber: string;
        message: string;
    }) =>
        this.client
            .post<{
                messageId: string;
                requestID: RequestID;
                phoneNumber: string;
                result: boolean;
            }>(`/contact`, data)
            .then((response) => response.data);

    /**
     * Activates user account in Upscore backend.
     * TODO: Add types
     *
     * Domain is implicilty added to POST data
     */
    public postUserActivate = (data: {
        altinnData?: any;
        marketingConsent?: any;
        formData?: any;
        role?: string;
    }) => {
        const { formData, role } = data;
        return this.client
            .post<{
                messageId: string;
                mutated: User;
                requestID: RequestID;
                result: boolean;
            }>(`/user/activate`, {
                ...formData,
                ...(role ? { role } : {}),
                domain: window.location.hostname,
            })
            .then((response) => response.data);
    };

    /**
     * This endpoint is currently used only on `Popup Offers` contact forms.
     */
    public postUserOffer = (data: {
        name: string;
        email: string;
        phoneNumber: number;
        zip: string;
        message: string;
        partner: string;
        requestID: RequestID;
    }) =>
        this.client
            .post<{
                requestID: RequestID;
                result: {
                    messageId: string;
                    user: User;
                };
            }>(`/user/offer`, data)
            .then((response) => response.data);

    /**
     * This request is sent whenever user decides to display primary or
     * secondary offers in `Offers Popup`
     */
    public postUserPaymentRemark = (data: {
        requestID: RequestID;
        paymentRemark: 0 | 1;
    }) =>
        this.client
            .post<{
                mutated: User;
                requestID: RequestID;
                result: boolean;
            }>(`/user/payment-remark`, data)
            .then((response) => response.data);

    public postUserTips = (data: { email: string; message: string }) =>
        this.client
            .post<{
                requestID: RequestID;
                result: {
                    messageId: string;
                    user: User;
                };
            }>(`/user/tips`, data)
            .then((reponse) => reponse.data);

    public postUserChallenge = (data: { email: string; message: string }) =>
        this.client
            .post<{
                requestID: RequestID;
                result: {
                    messageId: string;
                    user: User;
                };
            }>(`/user/challenge`, data)
            .then((reponse) => reponse.data);

    /**
     * Saves user profile to the backends with form data.
     */
    public postUserProfile = (data: {
        name: string;
        email: string;
        phoneNumber?: string;
        // TODO: Check if types fits
        sources: Source[];
    }) =>
        this.client
            .post<{
                mutated: User;
                requestID: RequestID;
                result: boolean;
            }>(`/user/profile`, data)
            .then((response) => response.data);

    /**
     * Removes user profile from Upscore backend.
     */
    public deleteUserProfile = () =>
        this.client
            .delete<{ requestID: RequestID; result: boolean }>(`/user/profile`)
            .then((response) => response.data);

    public theiaAuth = () =>
        this.client
            .post<{
                token: string;
                expire_at: string;
            }>(`/theia-auth/1.0/auth`)
            .then((response) => {
                sessionStorage.setItem('theia-token', response.data.token);
                return response.data;
            });

    public theiaLoanApplications = (params: { id: string; token: string }) =>
        this.client
            // .get(`/theia-api/1.0/loan_applications/${params.id}`)
            .get<{
                tax_documents: {
                    personal: {
                        tax_reports: Array<{
                            content: string;
                            filename: string;
                            mime_type: string;
                        }>;
                    };
                };
            }>(
                `/theia-api/1.0/loan_applications/${params.id}`,
            )
            .then((response) => {
                return response.data;
            });
}
